import { create, StoreApi } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../services/authService'
import { UserRole } from '../types/auth.types'
import type {
  AuthState,
  UserResponse,
  CompanyResponse,
  LoginFormData,
  RegisterFormData,
  UserUpdateRequest,
  PasswordChangeRequest,
  Token
} from '../types/auth.types'

// 持久化状态类型（只包含需要存储到localStorage的字段）
interface PersistedAuthState {
  user: UserResponse | null
  company: CompanyResponse | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
}

// 认证存储接口
interface AuthStore extends AuthState {
  // 动作
  login: (formData: LoginFormData) => Promise<void>
  register: (formData: RegisterFormData) => Promise<void>
  logout: () => Promise<void>
  doRefreshToken: () => Promise<void>
  refreshUserInfo: () => Promise<void>  // 新增：刷新用户和公司信息
  updateUser: (userId: number, updateData: UserUpdateRequest) => Promise<void>
  changePassword: (userId: number, passwordData: PasswordChangeRequest) => Promise<void>
  updateProfile: (user: Partial<UserResponse>) => void
  clearError: () => void
  setLoading: (isLoading: boolean) => void
  // 工具函数
  isAdmin: () => boolean
  hasPermission: (permission: string) => boolean
}

// 创建认证存储
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      user: null,
      company: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isRestored: false,

      // 登录
      login: async (formData: LoginFormData) => {
        set({ isLoading: true, error: null })
        try {
          // 调用实际的API服务
          const result = await authService.login(formData)

          // authService.login已经将令牌存储到localStorage
          // 从返回结果中提取用户、公司和令牌信息
          const { user, company, token } = result

          set({
            user,
            company,
            token: token.access_token,
            refreshToken: token.refresh_token || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isRestored: true
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '登录失败'
          })
          throw error
        }
      },

      // 注册
      register: async (formData: RegisterFormData) => {
        set({ isLoading: true, error: null })
        try {
          // 移除前端特有的confirmPassword字段
          const { confirmPassword, ...registerData } = formData
          // 调用实际的API服务
          const result = await authService.register(registerData)

          // authService.register已经将令牌存储到localStorage
          // 从返回结果中提取用户、公司和令牌信息
          const { user, company, token } = result

          set({
            user,
            company,
            token: token.access_token,
            refreshToken: token.refresh_token || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isRestored: true
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '注册失败'
          })
          throw error
        }
      },

      // 退出登录
      logout: async () => {
        set({ isLoading: true })
        try {
          // 调用实际的API退出登录
          await authService.logout()

          // authService.logout已经清除localStorage中的令牌
          // 更新本地状态
          set({
            user: null,
            company: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            isRestored: true
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '退出登录失败'
          })
          throw error
        }
      },

      // 刷新令牌
      doRefreshToken: async () => {
        // 检查是否有刷新令牌
        const { refreshToken } = get()
        if (!refreshToken) {
          throw new Error('没有可用的刷新令牌')
        }

        set({ isLoading: true })
        try {
          // 调用实际的刷新令牌API
          // authService.refreshToken会从localStorage读取refreshToken并更新存储
          const token = await authService.refreshToken()

          // 更新本地状态
          set({
            token: token.access_token,
            refreshToken: token.refresh_token || null,
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '令牌刷新失败'
          })
          throw error
        }
      },

      // 刷新用户和公司信息（从后端重新获取最新数据）
      // 401 自动 refresh token 由 client.ts 拦截器处理
      refreshUserInfo: async () => {
        const { token, isAuthenticated } = get()

        if (!isAuthenticated || !token) {
          return
        }

        try {
          const { user, company } = await authService.getCurrentUser()

          set({
            user,
            company,
            error: null
          })
        } catch {
          // 401 时 client.ts 拦截器已处理 refresh/跳转，此处无需额外操作
        }
      },

      // 更新用户信息
      updateUser: async (userId: number, updateData: UserUpdateRequest) => {
        set({ isLoading: true, error: null })
        try {
          // 调用实际的API服务
          const updatedUser = await authService.updateUser(userId, updateData)

          // 更新本地状态
          set({
            user: updatedUser,
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '更新用户信息失败'
          })
          throw error
        }
      },

      // 修改密码
      changePassword: async (userId: number, passwordData: PasswordChangeRequest) => {
        set({ isLoading: true, error: null })
        try {
          // 调用实际的API服务
          await authService.changePassword(userId, passwordData)

          set({
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '修改密码失败'
          })
          throw error
        }
      },

      // 更新个人资料（本地更新）
      updateProfile: (user: Partial<UserResponse>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...user } : null
        }))
      },

      // 清除错误
      clearError: () => {
        set({ error: null })
      },

      // 设置加载状态
      setLoading: (isLoading: boolean) => {
        set({ isLoading })
      },

      // 检查是否是管理员（超级管理员或公司管理员）
      isAdmin: () => {
        const { user } = get()
        return user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.COMPANY_ADMIN
      },

      // 检查是否有权限
      hasPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false

        // 简单的权限检查逻辑
        // 实际项目中应该根据用户角色检查权限
        const superAdminPermissions = ['read', 'write', 'delete', 'manage_users', 'manage_company', 'manage_system']
        const companyAdminPermissions = ['read', 'write', 'delete', 'manage_users', 'manage_company']
        const userPermissions = ['read', 'write_own', 'view_own_conversations', 'send_messages']

        if (user.role === UserRole.SUPER_ADMIN) {
          return superAdminPermissions.includes(permission)
        } else if (user.role === UserRole.COMPANY_ADMIN) {
          return companyAdminPermissions.includes(permission)
        } else if (user.role === UserRole.USER) {
          return userPermissions.includes(permission)
        }

        return false
      }
    }),
    {
      name: 'auth-storage', // localStorage的key
      partialize: (state): PersistedAuthState => ({
        user: state.user,
        company: state.company,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      }),
      onRehydrateStorage: () => {
        // 从localStorage恢复数据时的回调
        return (state: AuthStore | undefined, error: unknown) => {
          if (error) {
            console.error('恢复认证状态失败:', error)
          } else if (state) {
            // 状态恢复完成，设置isRestored标志
            state.isRestored = true
          }
        }
      }
    }
  )
)

// 导出一些常用的选择器
export const useCurrentUser = () => useAuthStore((state) => state.user)
export const useCurrentCompany = () => useAuthStore((state) => state.company)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useAuthLoading = () => useAuthStore((state) => state.isLoading)
export const useAuthError = () => useAuthStore((state) => state.error)
export const useIsRestored = () => useAuthStore((state) => state.isRestored)